import { fetchRankingTypes, fetchTeamRankings, fetchTeamsOverTime } from '#/queries'
import { TeamAvatar } from '#/components/team-avatar'
import { Tooltip } from '#/components/ui/tooltip'
import { useMediaQuery } from '#/helpers/use-media-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Link } from '#/components/link'
import { Tabs } from '#/components/routed-tabs'
import { TeamsBarRace } from '#/components/animated-teams'
import { optionalString } from '#/helpers/search-params'
import { SITE_NAME, seo } from '#/helpers/seo'
import { SortableTh, useTableSort } from '#/components/sortable-table'

function RankChange({ change, isNew }: { change: number | null | undefined; isNew?: boolean }) {
  if (isNew) return <span className="text-sm text-green-600 dark:text-green-400">NEW</span>
  if (change == null) return <span className="text-sm text-blue-600 dark:text-blue-400">RE</span>
  if (change === 0) return <span className="text-sm text-muted-foreground">-</span>
  if (change > 0) return <span className="text-sm text-green-600 dark:text-green-400">↑{change}</span>
  return <span className="text-sm text-red-600 dark:text-red-400">↓{Math.abs(change)}</span>
}

export const Route = createFileRoute('/team-rankings')({
  validateSearch: (search: Record<string, unknown>) => ({
    typeCode: optionalString(search.typeCode),
  }),
  staticData: { title: 'Team Rankings' },
  beforeLoad: (context) => {
    if (!context.search.typeCode)
      throw redirect({ to: '/team-rankings', search: { typeCode: 'ROLLING_YEAR' } })
  },
  // Search params are only part of the loader's cache key if they're declared here,
  // otherwise switching ranking type reuses the stale loader data.
  loaderDeps: ({ search: { typeCode } }) => ({ typeCode: typeCode ?? 'ROLLING_YEAR' }),
  loader: async ({ deps: { typeCode } }) => {
    const [rankingTypes, rankings, teamsOverTime] = await Promise.all([
      fetchRankingTypes(),
      fetchTeamRankings({ data: { typeCode } }),
      fetchTeamsOverTime({ data: { typeCode } }),
    ])
    return { rankingTypes, rankings, teamsOverTime }
  },
  head: () =>
    seo({
      title: `Team Rankings — ${SITE_NAME}`,
      description: 'UK Malifaux team rankings — rolling-year leaderboard.',
      path: '/team-rankings',
    }),
  component: RouteComponent,
})

function RouteComponent() {
  const { rankingTypes, rankings, teamsOverTime } = Route.useLoaderData()
  const { typeCode = 'ROLLING_YEAR' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const isMd = useMediaQuery('(min-width: 992px)')
  const rankingDescription = rankingTypes.find((rt) => rt.code === typeCode)?.description
  const { rows, getSortProps } = useTableSort(rankings as any[], {
    rank: { value: (t) => t.rank },
    change: { value: (t) => (t.new_team ? null : t.rank_change), natural: 'desc' },
    team: { value: (t) => t.team_name },
    points: { value: (t) => t.total_points, natural: 'desc' },
    players: { value: (t) => t.player_count, natural: 'desc' },
    events: { value: (t) => t.event_count, natural: 'desc' },
  })

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <select
          className="w-[200px] rounded border border-border px-2 py-1 text-sm"
          value={typeCode}
          onChange={(e) => navigate({ search: (prev) => ({ ...prev, typeCode: e.target.value || undefined }) })}
        >
          {rankingTypes.map((rt) => (
            <option key={rt.code} value={rt.code}>{rt.name}</option>
          ))}
        </select>
        {rankingDescription && <p>{rankingDescription}</p>}
      </div>
      <Tabs defaultValue="table">
        <Tabs.List>
          <Tabs.Tab value="table">Table View</Tabs.Tab>
          <Tabs.Tab value="animation">Animation</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="table">
          <p className="mb-2 text-sm text-muted-foreground">
            Team ranking points are calculated as the sum of the ranking points of the top five players in the team.
          </p>
          <div
            className="overflow-x-auto"
            style={
              !isMd
                ? {
                    maskImage: 'linear-gradient(to right, black 80%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to right, black 80%, transparent 100%)',
                  }
                : {}
            }
          >
            <table className="min-w-full text-sm tabular-nums">
              <thead className="sticky top-0 bg-surface">
                <tr className="border-b border-border text-left">
                  <SortableTh {...getSortProps('rank')}>Rank</SortableTh>
                  <SortableTh {...getSortProps('change')}>Change</SortableTh>
                  <th className="px-2 py-2 font-semibold">Logo</th>
                  <SortableTh {...getSortProps('team')}>Team</SortableTh>
                  <SortableTh {...getSortProps('points')}>Total Points</SortableTh>
                  <SortableTh {...getSortProps('players')}>Players</SortableTh>
                  <SortableTh {...getSortProps('events')}>Events</SortableTh>
                </tr>
              </thead>
              <tbody>
                {rows.map((team: any) => (
                  <tr key={team.team_id} className="border-b border-border">
                    <td className="whitespace-nowrap px-2 py-1.5">{team.rank}</td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      <RankChange change={team.rank_change} isNew={team.new_team} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Tooltip label={team.team_name}>
                        <TeamAvatar image_key={team.image_key} name={team.team_name} />
                      </Tooltip>
                    </td>
                    <td className="px-2 py-1.5">
                      <Link to="/team/$id" params={{ id: String(team.team_id) }} search={{ tab: undefined }}>
                        {team.team_name}
                      </Link>
                    </td>
                    <td className="px-2 py-1.5">{team.total_points.toFixed(2)}</td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {team.player_count != null ? `${team.player_count}/5` : '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {team.event_count != null ? `${team.event_count}/25` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="animation">
          <TeamsBarRace data={teamsOverTime as any} />
        </Tabs.Panel>
      </Tabs>
    </div>
  )
}
